import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Row from "react-bootstrap/Row";
import accountsApi from "../../services/accountsCommonService";
import AccountsConstants from "../../common/AccountsConstants";
import { formatINR, monthStartInput, todayInput } from "./accountUser";
import { Field, controlClass, selectClass } from "./FormFields";
import { validateVoucher } from "./validate";
import VoucherEntry, { EntryPreview, settlementLink } from "./VoucherEntry";
import { CancelIcon, ViewIcon } from "./AccountIcons";
import LedgerCombo from "./LedgerCombo";
import { StatusChip } from "./AccountWidgets";
import PageShell from "./PageShell";
import useAccountMeta from "./useAccountMeta";
import "./accounts.css";

function particularsOf(voucher) {
  const debit = (voucher.lines || [])
    .filter((line) => Number(line.debit) > 0)
    .map((line) => line.ledgerName)
    .filter(Boolean)
    .join(", ");
  const credit = (voucher.lines || [])
    .filter((line) => Number(line.credit) > 0)
    .map((line) => line.ledgerName)
    .filter(Boolean)
    .join(", ");
  if (debit && credit) return `Dr ${debit} / To ${credit}`;
  return debit || credit || "";
}

export function VoucherForm({ voucherType }) {
  if (
    voucherType === "RECEIPT" ||
    voucherType === "PAYMENT" ||
    voucherType === "PURCHASE"
  ) {
    return <VoucherEntry voucherType={voucherType} />;
  }
  if (voucherType === "SALES") {
    return <SalesEntryMoved />;
  }
  return <LegacyVoucherForm voucherType={voucherType} />;
}

function SalesEntryMoved() {
  const navigate = useNavigate();
  return (
    <PageShell
      title="Sales Invoice"
      subtitle="Invoices are raised from a loading advice"
    >
      <button
        type="button"
        className="btn btn-primary"
        onClick={() => navigate(AccountsConstants.LOADING_ADVICES)}
      >
        Open loading advice
      </button>
    </PageShell>
  );
}

function LegacyVoucherForm({ voucherType }) {
  const navigate = useNavigate();
  const [parties, setParties] = useState([]);
  const [counters, setCounters] = useState([]);
  const [partyLedgerId, setPartyLedgerId] = useState("");
  const [counterLedgerId, setCounterLedgerId] = useState("");
  const [voucherDate, setVoucherDate] = useState(todayInput());
  const [dueDate, setDueDate] = useState(todayInput());
  const [referenceNo, setReferenceNo] = useState("");
  const [narration, setNarration] = useState("");
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("CASH");
  const [cheque, setCheque] = useState({
    chequeNo: "",
    chequeDate: todayInput(),
    drawerBankName: "",
    drawerBranch: "",
  });
  const [invoices, setInvoices] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [saved, setSaved] = useState(null);
  const [options, setOptions] = useState(null);

  const { meta, error: metaError, labelOf } = useAccountMeta();
  const money = ["RECEIPT", "PAYMENT"].includes(voucherType);
  const typeInfo = (meta?.voucherTypes || []).find(
    (item) => item.value === voucherType,
  );

  useEffect(() => {
    accountsApi
      .lookups()
      .then((result) => setOptions(result?.data || {}))
      .catch((err) => setError(err.message));
  }, []);

  useEffect(() => {
    if (!options || !typeInfo) return;
    setParties(options[typeInfo.partyLookup] || []);
    const counterKey =
      typeInfo.counterLookup ||
      (meta?.paymentModes || []).find((item) => item.value === mode)
        ?.ledgerLookup ||
      "";
    const rows = options[counterKey] || [];
    const preferred =
      voucherType === "SALES"
        ? "Sales A/c"
        : voucherType === "PURCHASE"
          ? "Purchase A/c"
          : mode === "CASH"
            ? "Cash"
            : "Bank";
    const picked = rows.find((row) => row.name === preferred) || rows[0];
    setCounters(rows);
    setCounterLedgerId((current) =>
      rows.some((row) => row._id === current) ? current : picked?._id || "",
    );
  }, [options, typeInfo, mode, meta, voucherType]);

  useEffect(() => {
    if (!money || !partyLedgerId) {
      setInvoices([]);
      return;
    }
    accountsApi
      .outstanding(partyLedgerId)
      .then((result) => setInvoices(result?.data || []))
      .catch((err) => setError(err.message));
  }, [money, partyLedgerId]);

  const partyName =
    parties.find((row) => row._id === partyLedgerId)?.name || "Party";
  const counterName =
    counters.find((row) => row._id === counterLedgerId)?.name ||
    (voucherType === "SALES" ? "Sales A/c" : "Purchase A/c");
  const numericAmount = Number(amount) || 0;
  const allocated = Object.values(allocations).reduce(
    (sum, value) => sum + (Number(value) || 0),
    0,
  );
  const postDated =
    money && mode === "CHEQUE" && cheque.chequeDate > voucherDate;

  const preview = useMemo(() => {
    if (voucherType === "SALES")
      return [
        ["Dr", partyName, numericAmount],
        ["To", counterName, numericAmount],
      ];
    if (voucherType === "PURCHASE")
      return [
        ["Dr", counterName, numericAmount],
        ["To", partyName, numericAmount],
      ];
    if (voucherType === "RECEIPT")
      return [
        ["Dr", counterName, numericAmount],
        ["To", partyName, numericAmount],
      ];
    return [
      ["Dr", partyName, numericAmount],
      ["To", counterName, numericAmount],
    ];
  }, [voucherType, partyName, counterName, numericAmount]);

  const autoAllocate = () => {
    let left = numericAmount;
    const next = {};
    invoices.forEach((invoice) => {
      const take = Math.min(left, Number(invoice.outstanding) || 0);
      if (take > 0) next[invoice.voucherId] = take.toFixed(2);
      left = Number((left - take).toFixed(2));
    });
    setAllocations(next);
  };

  const save = async (event) => {
    event.preventDefault();
    setError("");
    setSaved(null);
    const nextErrors = validateVoucher({
      voucherType,
      partyLedgerId,
      counterLedgerId,
      voucherDate,
      dueDate,
      amount,
      mode,
      cheque,
      allocated,
      today: todayInput(),
    });
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    const body = {
      voucherType,
      voucherDate,
      partyLedgerId,
      counterLedgerId: counterLedgerId || undefined,
      referenceNo,
      narration,
      amount: numericAmount,
    };
    if (!money) body.dueDate = dueDate;
    if (money) {
      body.mode = mode;
      body.allocations = invoices
        .map((invoice) => ({
          againstVoucherId: invoice.voucherId,
          amount: Number(allocations[invoice.voucherId] || 0),
        }))
        .filter((row) => row.amount > 0);
      if (mode === "CHEQUE") body.cheque = { ...cheque, amount: numericAmount };
    }
    try {
      const result = await accountsApi.saveVoucher(body);
      setSaved(result.data);
    } catch (err) {
      setError(err.message);
    }
  };

  const download = async () => {
    try {
      const blob = await accountsApi.receiptPdf(saved._id);
      const url = URL.createObjectURL(blob);
      window.open(url);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <PageShell
      title={labelOf("voucherTypes", voucherType) || voucherType}
      subtitle="Double-entry preview is shown before posting"
    >
      <form className="accounts-form" onSubmit={save} noValidate>
        {(error || metaError) && (
          <div className="accounts-error">{error || metaError}</div>
        )}
        {fieldErrors.allocations && (
          <div className="accounts-error">{fieldErrors.allocations}</div>
        )}
        {saved && (
          <div className="accounts-actions">
            <strong>Posted {saved.voucherNo}</strong>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() =>
                navigate(`${AccountsConstants.VOUCHER_VIEW}?id=${saved._id}`)
              }
            >
              <i className="bi bi-eye" /> View
            </button>
            {voucherType === "RECEIPT" && (
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={download}
              >
                Print / Download receipt
              </button>
            )}
          </div>
        )}
        <Row>
          <Field label="Party" required error={fieldErrors.partyLedgerId}>
            <select
              className={selectClass(fieldErrors.partyLedgerId)}
              value={partyLedgerId}
              onChange={(event) => setPartyLedgerId(event.target.value)}
            >
              <option value="">Select</option>
              {parties.map((row) => (
                <option key={row._id} value={row._id}>
                  {row.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Date" required error={fieldErrors.voucherDate}>
            <input
              className={controlClass(fieldErrors.voucherDate)}
              type="date"
              max={todayInput()}
              value={voucherDate}
              onChange={(event) => setVoucherDate(event.target.value)}
            />
          </Field>
          {!money && (
            <Field label="Due date" required error={fieldErrors.dueDate}>
              <input
                className={controlClass(fieldErrors.dueDate)}
                type="date"
                min={voucherDate}
                value={dueDate}
                onChange={(event) => setDueDate(event.target.value)}
              />
            </Field>
          )}
          <Field label="Reference">
            <input
              className="form-control"
              value={referenceNo}
              onChange={(event) => setReferenceNo(event.target.value)}
            />
          </Field>
          <Field label="Amount" required error={fieldErrors.amount}>
            <input
              className={controlClass(fieldErrors.amount)}
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </Field>
          {money && (
            <Field label="Mode" required>
              <select
                className="form-select"
                value={mode}
                onChange={(event) => setMode(event.target.value)}
              >
                <option value="">Select</option>
                {(meta?.paymentModes || []).map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field
            label={
              money
                ? mode === "CASH"
                  ? "Cash ledger"
                  : "Bank ledger"
                : voucherType === "SALES"
                  ? "Sales ledger"
                  : "Purchase ledger"
            }
            required
            error={fieldErrors.counterLedgerId}
          >
            <select
              className={selectClass(fieldErrors.counterLedgerId)}
              value={counterLedgerId}
              onChange={(event) => setCounterLedgerId(event.target.value)}
            >
              <option value="">Select</option>
              {counters.map((row) => (
                <option key={row._id} value={row._id}>
                  {row.name}
                </option>
              ))}
            </select>
          </Field>
          {money && mode === "CHEQUE" && (
            <>
              <Field label="Cheque no" required error={fieldErrors.chequeNo}>
                <input
                  className={controlClass(fieldErrors.chequeNo)}
                  value={cheque.chequeNo}
                  onChange={(event) =>
                    setCheque({ ...cheque, chequeNo: event.target.value })
                  }
                />
              </Field>
              <Field
                label="Cheque date"
                required
                error={fieldErrors.chequeDate}
              >
                <input
                  className={controlClass(fieldErrors.chequeDate)}
                  type="date"
                  value={cheque.chequeDate}
                  onChange={(event) =>
                    setCheque({ ...cheque, chequeDate: event.target.value })
                  }
                />
              </Field>
              <Field
                label="Drawer bank"
                required={voucherType === "RECEIPT"}
                error={fieldErrors.drawerBankName}
              >
                <input
                  className={controlClass(fieldErrors.drawerBankName)}
                  value={cheque.drawerBankName}
                  onChange={(event) =>
                    setCheque({ ...cheque, drawerBankName: event.target.value })
                  }
                />
              </Field>
              <Field label="Branch">
                <input
                  className="form-control"
                  value={cheque.drawerBranch}
                  onChange={(event) =>
                    setCheque({ ...cheque, drawerBranch: event.target.value })
                  }
                />
              </Field>
            </>
          )}
          <Field label="Narration" md={12} lg={12}>
            <textarea
              className="form-control"
              rows={3}
              maxLength={500}
              value={narration}
              onChange={(event) => setNarration(event.target.value)}
            />
          </Field>
        </Row>
        {postDated && (
          <p className="subText">
            This cheque is post-dated. It cannot be deposited before the cheque
            date.
          </p>
        )}
        {money && (
          <div>
            <div className="accounts-actions">
              <h3>Allocate</h3>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={autoAllocate}
              >
                Auto-allocate oldest first
              </button>
              <span>
                Allocated {formatINR(allocated)} / Advance{" "}
                {formatINR(numericAmount - allocated)}
              </span>
            </div>
            <div className="accounts-table-wrap">
              <table className="accounts-table">
                <thead>
                  <tr>
                    <th>Voucher</th>
                    <th>Date</th>
                    <th>Due</th>
                    <th className="num">Amount</th>
                    <th className="num">Outstanding</th>
                    <th>Allocate</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.voucherId}>
                      <td>{invoice.voucherNo}</td>
                      <td>{invoice.voucherDate}</td>
                      <td>{invoice.dueDate}</td>
                      <td className="num">{formatINR(invoice.amount)}</td>
                      <td className="num">{formatINR(invoice.outstanding)}</td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={allocations[invoice.voucherId] || ""}
                          onChange={(event) =>
                            setAllocations({
                              ...allocations,
                              [invoice.voucherId]: event.target.value,
                            })
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <div className="accounts-preview">
          {preview.map((line) => (
            <div key={line[0] + line[1]}>
              {line[0] === "Dr" ? "Dr" : "  To"} {line[1]} {formatINR(line[2])}
            </div>
          ))}
        </div>
        <div className="accounts-form-actions">
          <button className="btn btn-primary" type="submit">
            Post Voucher
          </button>
        </div>
      </form>
    </PageShell>
  );
}

export function VoucherList() {
  const navigate = useNavigate();
  const { meta, labelOf } = useAccountMeta();
  const [rows, setRows] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [filters, setFilters] = useState({
    voucherType: "",
    status: "",
    fromDate: monthStartInput(),
    toDate: todayInput(),
    ledgerId: "",
    search: "",
  });
  const [error, setError] = useState("");
  const [cancelId, setCancelId] = useState(null);
  const [reason, setReason] = useState("");

  const load = async (next = filters) => {
    setError("");
    try {
      const result = await accountsApi.vouchers({
        ...next,
        search: next.search.trim(),
        per_page: 100,
      });
      setRows(result?.data || []);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    load();
    accountsApi
      .ledgers()
      .then((result) => setLedgers(result?.data || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFilter = (key) => (event) => {
    const next = { ...filters, [key]: event.target.value };
    setFilters(next);
    load(next);
  };

  const cancel = async () => {
    if (!reason.trim()) return;
    try {
      await accountsApi.cancelVoucher(cancelId, reason.trim());
      setCancelId(null);
      setReason("");
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <PageShell
      title="Voucher List"
    >
      <div className="accounts-panel accounts-filter-grid">
        <label>
          Type
          <select
            className="form-select"
            value={filters.voucherType}
            onChange={setFilter("voucherType")}
          >
            <option value="">All types</option>
            {(meta?.voucherTypes || []).map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Status
          <select
            className="form-select"
            value={filters.status}
            onChange={setFilter("status")}
          >
            <option value="">All statuses</option>
            {(meta?.voucherStatuses || []).map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          From date
          <input
            className="form-control"
            type="date"
            value={filters.fromDate}
            onChange={setFilter("fromDate")}
          />
        </label>
        <label>
          To date
          <input
            className="form-control"
            type="date"
            value={filters.toDate}
            onChange={setFilter("toDate")}
          />
        </label>
        <LedgerCombo
          label="Ledger"
          placeholder="All ledgers"
          groups={meta?.ledgerGroups || []}
          ledgers={ledgers}
          value={filters.ledgerId}
          onChange={(id) => {
            const next = { ...filters, ledgerId: id };
            setFilters(next);
            load(next);
          }}
        />
        <label>
          Voucher / reference no
          <input
            className="form-control"
            value={filters.search}
            placeholder="Voucher or reference number"
            onChange={setFilter("search")}
          />
        </label>
        <button
          type="button"
          className="btn btn-link"
          onClick={() => {
            const empty = {
              voucherType: "",
              status: "",
              fromDate: monthStartInput(),
              toDate: todayInput(),
              ledgerId: "",
              search: "",
            };
            setFilters(empty);
            load(empty);
          }}
        >
          Clear filters
        </button>
      </div>
      {error && <div className="accounts-error">{error}</div>}
      <div className="accounts-table-wrap">
        <table className="accounts-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Voucher No</th>
              <th>Type</th>
              <th>Particulars</th>
              <th>Reference</th>
              <th className="num">Amount</th>
              <th>Status</th>
              <th>Next step</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((voucher) => {
              const payLink =
                voucher.voucherType === "PURCHASE"
                  ? settlementLink("PURCHASE", voucher)
                  : "";
              const receiptLink =
                voucher.voucherType === "SALES"
                  ? settlementLink("SALES", voucher)
                  : "";
              const settled =
                voucher.paymentStatus &&
                (voucher.voucherType === "PURCHASE" ||
                  voucher.voucherType === "SALES");
              return (
                <tr key={voucher._id}>
                  <td>{voucher.voucherDate}</td>
                  <td>
                    <button
                      type="button"
                      className="accounts-voucher-link"
                      title="View voucher details"
                      onClick={() =>
                        navigate(
                          `${AccountsConstants.VOUCHER_VIEW}?id=${voucher._id}`,
                        )
                      }
                    >
                      <span>{voucher.voucherNo}</span>
                    </button>
                  </td>
                  <td>{labelOf("voucherTypes", voucher.voucherType)}</td>
                  <td>{particularsOf(voucher)}</td>
                  <td>{voucher.referenceNo}</td>
                  <td className="num">
                    {formatINR(voucher.totalDebit || voucher.amount)}
                  </td>
                  <td>
                    {settled ? (
                      <StatusChip
                        status={voucher.paymentStatus}
                        kind="invoice"
                      />
                    ) : (
                      <span
                        className={`accounts-chip ${voucher.status === "POSTED" ? "ok" : "bad"}`}
                      >
                        {labelOf("voucherStatuses", voucher.status)}
                      </span>
                    )}
                  </td>
                  <td className="accounts-next">
                    {payLink && (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => navigate(payLink)}
                      >
                        Create Payment
                      </button>
                    )}
                    {receiptLink && (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => navigate(receiptLink)}
                      >
                        Create Receipt
                      </button>
                    )}
                    <ViewIcon
                      onClick={() =>
                        navigate(
                          `${AccountsConstants.VOUCHER_VIEW}?id=${voucher._id}`,
                        )
                      }
                    />
                    <CancelIcon
                      disabled={voucher.status !== "POSTED"}
                      onClick={() => {
                        setCancelId(voucher._id);
                        setReason("");
                      }}
                    />
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8}>No vouchers match these filters.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {cancelId && (
        <div className="accounts-panel accounts-cancel">
          <div>
            <h3>Cancel voucher</h3>
            <p className="accounts-hint">
              The voucher stays on record and is removed from balances. This
              cannot be undone.
            </p>
          </div>
          <label className="tally-field">
            Reason *
            <textarea
              className="form-control"
              rows={3}
              maxLength={500}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          <div className="accounts-cancel-actions">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setCancelId(null)}
            >
              Keep voucher
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={!reason.trim()}
              onClick={cancel}
            >
              Cancel voucher
            </button>
          </div>
        </div>
      )}
    </PageShell>
  );
}

export function VoucherView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const voucherId = searchParams.get("id");
  const [voucher, setVoucher] = useState(null);
  const [reason, setReason] = useState("");
  const [openCancel, setOpenCancel] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    accountsApi
      .voucher(voucherId)
      .then((result) => setVoucher(result.data))
      .catch((err) => setError(err.message));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucherId]);

  const cancel = async () => {
    if (!reason.trim()) return;
    setError("");
    try {
      await accountsApi.cancelVoucher(voucherId, reason.trim());
      setOpenCancel(false);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  if (!voucher) {
    return (
      <PageShell title="Voucher">
        <div>{error || "Loading..."}</div>
      </PageShell>
    );
  }

  const titles = {
    RECEIPT: "Receipt Voucher",
    PAYMENT: "Payment Voucher",
    PURCHASE: "Purchase Voucher",
    SALES: "Sales Invoice",
    CREDIT_NOTE: "Credit Note",
    DEBIT_NOTE: "Debit Note",
    JOURNAL: "Journal Voucher",
  };
  const modeLabel =
    voucher.paymentMode || voucher.mode
      ? voucher.voucherType === "PAYMENT"
        ? "Payment Mode"
        : "Receipt Mode"
      : "Due date";
  const modeValue =
    voucher.paymentMode || voucher.mode || voucher.dueDate || "—";
  return (
    <PageShell
      title={`${titles[voucher.voucherType] || voucher.voucherType} ${voucher.voucherNo}`}
    >
      <div className="accounts-form-actions start">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => navigate(AccountsConstants.VOUCHER_LIST)}
        >
          Back to list
        </button>
        {voucher.status === "POSTED" && (
          <button
            type="button"
            className="btn btn-outline-danger"
            onClick={() => setOpenCancel(true)}
          >
            <i className="bi bi-slash-circle" /> Cancel voucher
          </button>
        )}
      </div>
      {error && <div className="accounts-error">{error}</div>}
      {voucher.status === "CANCELLED" && (
        <div className="accounts-error">
          Cancelled{voucher.cancelledAt ? ` on ${voucher.cancelledAt}` : ""}.
          Reason: {voucher.cancelReason}. The voucher is kept for audit but
          removed from balances and reports.
        </div>
      )}
      <div className="accounts-panel">
        <div className="accounts-facts">
          <div>
            <span className="accounts-hint">Voucher No</span>
            <strong>{voucher.voucherNo}</strong>
          </div>
          <div>
            <span className="accounts-hint">Date</span>
            <strong>{voucher.voucherDate}</strong>
          </div>
          <div>
            <span className="accounts-hint">Reference No</span>
            <strong>{voucher.referenceNo || "—"}</strong>
          </div>
          <div>
            <span className="accounts-hint">{modeLabel}</span>
            <strong>{modeValue}</strong>
          </div>
        </div>
        <p>
          <span
            className={`accounts-chip ${voucher.status === "POSTED" ? "ok" : "bad"}`}
          >
            {voucher.status}
          </span>
        </p>
        {(voucher.items || []).length > 0 && (
          <div className="accounts-table-wrap">
            <table className="accounts-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="num">Quantity</th>
                  <th className="num">Rate</th>
                  <th className="num">Amount</th>
                </tr>
              </thead>
              <tbody>
                {voucher.items.map((item, index) => (
                  <tr key={index}>
                    <td>{item.description}</td>
                    <td className="num">
                      {item.quantity} {item.unit}
                    </td>
                    <td className="num">{formatINR(item.rate)}</td>
                    <td className="num">{formatINR(item.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <EntryPreview lines={voucher.lines || []} />
        <p className="accounts-narration">
          <span className="accounts-hint">Narration</span>
          {voucher.narration || "—"}
        </p>
      </div>
      {openCancel && (
        <div className="accounts-panel accounts-cancel">
          <div>
            <h3>Cancel voucher {voucher.voucherNo}</h3>
            <p className="accounts-hint">
              The voucher is kept for audit but removed from balances and
              reports. This cannot be undone.
            </p>
          </div>
          <label className="tally-field">
            Reason *
            <textarea
              className="form-control"
              rows={3}
              maxLength={500}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          <div className="accounts-cancel-actions">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setOpenCancel(false)}
            >
              Keep voucher
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={!reason.trim()}
              onClick={cancel}
            >
              Cancel voucher
            </button>
          </div>
        </div>
      )}
    </PageShell>
  );
}
